import React from "react";
import { useEffect, useState } from "react";
import { CryptoRewardType } from "../../models/User";
import { forEach } from "lodash";
import { useGetCryptosQuery } from "../../apis/cryptos.api";
import { CryptoCurrency } from "../../models/Wallet";
import { useSendPublicAddressMutation } from "../../apis/cryptocurrency-rewards.api";

function sortRewardList(rewards: CryptoRewardType[]){

  const waiting_rewards:CryptoRewardType[]= [];
  const inProgress_rewards:CryptoRewardType[] = [];
  const received_rewards:CryptoRewardType[] = [];

  forEach(rewards, (reward) => {

    if(reward.sent_to_wallet){
      received_rewards.push(reward);
    } else if (reward.wallet_public_address) {
      inProgress_rewards.push(reward);
    } else {
      waiting_rewards.push(reward);
    }

  })

  return {
    waiting: waiting_rewards,
    inProgress: inProgress_rewards,
    received: received_rewards
  }

}

interface CryptoRewardDrawerContentProps {
  rewards: CryptoRewardType[];
}

const CryptoRewardDrawerContent: React.FC<CryptoRewardDrawerContentProps> = ({rewards}) => {

  const receivedRewardListElement = [];
  const sortedRewardList = sortRewardList(rewards);

  return (

    <div className="flex flex-col gap-4">

      {
        !sortedRewardList.waiting.length &&
        !sortedRewardList.inProgress.length &&
        !!sortedRewardList.received.length &&
        <div className="text-center my-2">
          <p className="text-base font-normal">
            Your crypto reward has been sent!<br/>
            Continue to play to earn new crypto rewards.
          </p>
        </div>
      }

      {/* WAITING */}
      { !!sortedRewardList.waiting.length && sortedRewardList.waiting.map((reward, index) => {
        return <div className="mt-4" key={index}>
          <CryptoReward reward={reward}/>
          <WalletAddress isForm={true} />
        </div>

      }) }

      {/* IN PROGRESS */}
      { !!sortedRewardList.inProgress.length && sortedRewardList.inProgress.map((reward, index) => {
        return <div className="mt-4" key={index}>
          <CryptoReward reward={reward}/>
          <WalletAddress address={reward.wallet_public_address} />
        </div>
      }) }

      {/* RECEIVED */}
      { !!sortedRewardList.received.length && <div className="mt-4 flex flex-col gap-2">
          <h2 className="text-center">Crypto Rewards History</h2>
          { sortedRewardList.received.map((reward, index) => {
            return <CryptoReward reward={reward} key={index}/>
          }) }
        </div>
      }

    </div>

  );
}

export default CryptoRewardDrawerContent;

interface WalletAddressProps {
  isForm?: boolean;
  address?: string;
}

const WalletAddress: React.FC<WalletAddressProps> = ({isForm, address}) => {

  const [sendPublicAddress, { data: response, isLoading: isResponseLoading }] = useSendPublicAddressMutation();

  const [walletAddress, setWalletAddress] = useState("");
  const [walletErrored, setWalletErrored] = useState(false);

  const onSubmit = async () => {

    if (!walletAddress) {
      setWalletErrored(true);
      return;
    }

    try {
      const datas = {
        wallet_public_address: walletAddress
      };
      await sendPublicAddress(datas);
    } catch (err) {
      setWalletErrored(true);
    }

  }

  return(

    <div>

    { isForm && <div className="flex flex-col text-center gap-6 mt-2">

      <p className="font-medium text-base">To withdraw your crypto reward, <br/>copy and paste your public wallet address below.</p>

      <div className="px-1">
        <p className="text-sm text-left">Enter your Wallet public address (Polygon Chain)</p>
        <input
          className="seein-input"
          type="text"
          placeholder="0x9jdnx..."
          onChange={(event) => setWalletAddress(event.target.value)}
        />
        <span className="font-medium tracking-wide text-red-500 text-xs">
          {(walletErrored && "Please enter a valid wallet address.") || ""}
        </span>
      </div>

      <button className="orange-button text-white text-xs uppercase font-bold h-12 w-1/3 mx-auto" onClick={onSubmit}>SEND</button>

      <HelpBox/>

    </div>

    }

    { address && <div className="flex flex-col text-center gap-4 mt-2">

      <p className="font-medium text-base">Our team will proceed to send your crypto reward soon.</p>

      <div className="px-1">
        <p className="text-sm text-left">Your wallet public address (Polygon Chain)</p>
        <input type="text" value={address} readOnly={true}/>
      </div>

    </div>
    }

    </div>
  );
}

interface CryptoRewardProps {
  reward: CryptoRewardType
}

const CryptoReward: React.FC<CryptoRewardProps> = ({reward}) => {

  const {data: crypto, isLoading: isCryptoLoading} = useGetCryptosQuery({filter: reward.crypto_code, pageSize: 1, pageIndex: 0});
  const [selectedCrypto, setSelectedCrypto] = useState<CryptoCurrency>();

  function statusWording(reward: CryptoRewardType){

    let status = "";

    if(reward.sent_to_wallet){
      status = "Received";
    } else if (reward.wallet_public_address) {
      status = "In Progress";
    } else {
      status = "Waiting Address";
    }

    return status;
  }

  useEffect(()=>{
    if(crypto){
      setSelectedCrypto(crypto[0]);
    }
  }, [crypto]);

  return (<>

    { selectedCrypto && <div className="min-h-[68px] flat-card flex gap-2 border-2 shadow-none place-items-center">

      <div className="flex-none">
        <div className="w-8 h-8 p-0.5 bg-white rounded-full fat-grey-shadow-1 border border-grey-100 center flex">
          <img className="w-7 h-7 rounded-full" src={selectedCrypto.logo}/>
        </div>
      </div>

      <div className="flex flex-col grow">
        <p className="text-base">{reward.amount} {selectedCrypto.code}</p>
        { reward.sent_on &&
          <p className="text-sm text-gray-500">{reward.sent_on.toDateString()}</p>
        }
      </div>

      <div className="flex-none">
        <p className="text-orange text-base">{statusWording(reward)}</p>
      </div>

    </div>

    }

    </>
  );
}

const HelpBox: React.FC = () => {
  return (
    <div className="flex flex-col gap-3 px-4 mt-4">

      <h3>Having an issue?</h3>

      <div>
        <p className="font-normal">Your Public Address is not working</p>
        <p className="font-light">Make sure to use an ERC-20 compatible address, we prefer you use Polygon Chain (prevent from high gas fees)</p>
      </div>

      <div>
        <p className="font-normal">Your don't have a wallet</p>
        <p className="font-light">If you don't have a wallet, we recommend you use Metamask.io, the leading wallet app in the world.</p>

      </div>

      <div>
        <p className="font-normal">Your problem persist</p>
        <p className="font-light">Contact our support team at contact@tanjiblocks.com</p>
      </div>

    </div>
  );
}